import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, LayoutGrid, TrendingUp } from 'lucide-react';
import { RiskMatrix } from './RiskMatrix';
import { SafetyObjectiveLens } from './SafetyObjectiveLens';
import { EnrichedRiskArea } from '../../types/prevention';
import { RiskAreaDetailModal } from '../RiskAreaDetailModal';
import { RiskMethodologyModal } from '../RiskMethodologyModal';
import { RecommendationsView } from './RecommendationsView';
import { AnalyticsView } from './AnalyticsView';
import { BiltiChatPanel } from './BiltiChatPanel';
type PreventionSubTab = 'risk_matrix' | 'recommendations' | 'analytics';
export function PreventionView() {
  const [subTab, setSubTab] = useState<PreventionSubTab>('risk_matrix');
  const [safetyObjective, setSafetyObjective] = useState<string>('all');
  const [selectedArea, setSelectedArea] = useState<EnrichedRiskArea | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const handleUpdateStatus = (_areaId: string, _newStatus: string, _notes: string, _userName: string) => {
    // Status update — in production this would persist the change
  };
  return <div className="h-[calc(100vh-110px)] min-h-[600px] -mt-2 flex flex-col" data-id="element-1765">
      {/* Sub-navigation bar */}
      <div className="flex items-center justify-between px-6 py-1.5 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-1766">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg" data-id="element-1767">
          <button onClick={() => setSubTab('risk_matrix')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'risk_matrix' ? 'bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1768">
            <LayoutGrid size={15} className={subTab === 'risk_matrix' ? 'text-indigo-500' : 'text-gray-400'} data-id="element-1769" />
            Risk Matrix
          </button>
          <button onClick={() => setSubTab('recommendations')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'recommendations' ? 'bg-amber-50 text-amber-700 shadow-sm ring-1 ring-amber-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1770">
            <Lightbulb size={15} className={subTab === 'recommendations' ? 'text-amber-500' : 'text-gray-400'} data-id="element-1771" />
            Insights & Corrective Actions
          </button>
          <button onClick={() => setSubTab('analytics')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'analytics' ? 'bg-teal-50 text-teal-700 shadow-sm ring-1 ring-teal-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1772">
            <TrendingUp size={15} className={subTab === 'analytics' ? 'text-teal-500' : 'text-gray-400'} data-id="element-1773" />
            Trends
          </button>
        </div>

        {/* Safety Performance Lens - hidden for now */}
        {/* <SafetyObjectiveLens
          value={safetyObjective}
          onChange={setSafetyObjective}
         /> */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden" data-id="element-1774">
        <AnimatePresence mode="wait" data-id="element-1775">
          {subTab === 'risk_matrix' && <motion.div key="risk_matrix" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1776">
              <RiskMatrix onSelectArea={setSelectedArea} data-id="element-1777" />
            </motion.div>}
          {subTab === 'recommendations' && <motion.div key="recommendations" initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1778">
              <RecommendationsView data-id="element-1779" />
            </motion.div>}
          {subTab === 'analytics' && <motion.div key="analytics" initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1780">
              <AnalyticsView data-id="element-1781" />
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Detail Modal — shared across all tabs */}
      <RiskAreaDetailModal riskArea={selectedArea} onClose={() => setSelectedArea(null)} onUpdateStatus={handleUpdateStatus} onOpenMethodology={() => setShowMethodology(true)} data-id="element-1782" />
      {showMethodology && <RiskMethodologyModal onClose={() => setShowMethodology(false)} data-id="element-1783" />}

      {/* Floating Chat Panel */}
      <BiltiChatPanel tabContext={subTab} data-id="element-1784" />
    </div>;
}